import { TooltipContent } from '@app/components/various/DelwinNameAndDate/TooltipContent';
import { Calendar, ChevronDown, PreorderWithDates, Stock, StockWithDates } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { getDeliveryWindowById } from '@app/ducks';
import { getHumanTimePeriod, isRunning } from '@app/logic/deliveryWindows';
import { cn } from '@app/utils/cn';
import { isDefined, isNull } from '@app/utils/is';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useMatrixDeliveryWindowContext } from '../context';

interface Props {
  isDelwinExpired?: boolean;
  onClick?: () => void;
  isExpanded?: boolean;
}

export const MatrixHeader = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { isExpanded, onClick } = props;
  const { deliveryWindowId } = useMatrixDeliveryWindowContext();
  const { t } = useTranslation(['common']);

  const deliveryWindowSelector = React.useMemo(() => getDeliveryWindowById(deliveryWindowId), [deliveryWindowId]);
  const deliveryWindow = useSelector(deliveryWindowSelector);

  const preventDefault = (evt: React.UIEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
  };

  const Icon = React.useMemo(() => {
    if (!isDefined(deliveryWindow)) {
      return null;
    }
    const { deliveryPeriod } = getHumanTimePeriod(deliveryWindow);

    if (isRunning(deliveryWindow) || isNull(deliveryPeriod)) {
      return deliveryWindow.stockType === 'stock' ? Stock : Calendar;
    }

    return deliveryWindow.stockType === 'stock' ? StockWithDates : PreorderWithDates;
  }, [deliveryWindow]);

  const ariaLabel = isExpanded ? t('common:collapse', { item: deliveryWindow?.name }) : t('common:expand', { item: deliveryWindow?.name });

  return (
    <div
      className={cn('relative flex h-8 w-full justify-between rounded-t-regular bg-neutral-96', {
        'rounded-b-regular': !isExpanded,
      })}
    >
      {isDefined(deliveryWindow) && (
        <span className="mx-2 flex items-center gap-2 overflow-hidden">
          <Tooltip content={<TooltipContent deliveryWindow={deliveryWindow} />}>
            <div
              className={cn('z-matrix ml-1 mr-0.5 inline-flex cursor-pointer items-center')}
              role="presentation"
              onClick={preventDefault}
            >
              {Icon && <Icon />}
            </div>
          </Tooltip>
          <span className="overflow-hidden text-ellipsis text-nowrap body-1">{deliveryWindow.name}</span>
        </span>
      )}

      <button
        type="button"
        ref={ref}
        onClick={onClick}
        className="rounded-full absolute-cursor-pointer focus-visible:-outline-offset-1"
        aria-label={ariaLabel}
      >
        <ChevronDown className={cx('m-2 motion-safe:transition-transform', { 'rotate-180': isExpanded })} />
      </button>
    </div>
  );
});
